import React from 'react'
import { ChevronRight } from 'lucide-react'
import { mergeClasses } from '../../helpers/styling'
import { SvgIcon } from '../svg-icon'
import {
  AppAnalysis,
  CodeWidgetAnalysis,
  FINDING_CATALOGUE,
  findingsWatchedBy,
  FindingId,
  RiskId,
  RiskVerdict,
  Severity,
} from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { CHECK_TIME_LIMIT_MS, CodeWidgetCheck } from '../widgets/CodeWidget/analysis/use-code-widget-check'
import { Catalogue, catalogueEntry } from './catalogue'
import { FocusRequest, widgetLabel } from './focus'
import { AppTree } from './path-tree'
import { RiskSummaryRow, RowStatusIcon, SeverityPill, StatusLegend } from './status'
import { STATUS_STYLE } from './status-style'
import { WidgetSection } from './widget-section'

export type ShowFinding = (index: number, findingId: string | null, riskId: RiskId | null) => void

/** The scan runs in a worker: say when the report is older than the config, or when there is none. */
export function CheckProgress(props: { check: CodeWidgetCheck }) {
  const { check } = props
  if (check.state === 'ready') return null
  if (check.state === 'failed') {
    return (
      <p className={mergeClasses('mb-4 rounded-md px-3 py-2', STATUS_STYLE.notice)}>
        The scan could not run: {check.error}. This is a fault in the check, not in a page; the browser console has the
        details.
      </p>
    )
  }
  if (check.state === 'timed-out') {
    return (
      <p className={mergeClasses('mb-4 rounded-md px-3 py-2', STATUS_STYLE.notice)}>
        The scan did not finish within {CHECK_TIME_LIMIT_MS / 1000} seconds and was stopped
        {check.widget ? (
          <>
            {' '}
            while reading <span className="font-medium">{check.widget}</span>. A page that stalls the scanner is worth a
            look in itself.
          </>
        ) : (
          '. A page that stalls the scanner is worth a look in itself — start with the Code Widgets changed last.'
        )}
      </p>
    )
  }
  return (
    <p className="mb-4 text-xs text-muted-foreground">
      {check.analysis ? 'Checking the latest changes — showing the previous result.' : 'Checking the Code Widgets…'}
    </p>
  )
}

/**
 * An empty report has three quite different causes, and saying only "no Code Widgets" hides two of them:
 * the config may never have reached the store, or the widget type may be stored under a name the analyser
 * no longer matches. Listing what the walk did see separates them without anyone opening a console.
 */
export function EmptyState(props: { analysis: AppAnalysis; configLoaded: boolean }) {
  if (!props.configLoaded) {
    return (
      <p className="mt-6 text-muted-foreground">
        No app configuration is loaded, so nothing was scanned. Reopen this panel once the app has finished loading.
      </p>
    )
  }

  return (
    <div className="mt-6 text-muted-foreground">
      <p>None of the {props.analysis.widgetsScanned} widgets in this app is a Code Widget.</p>
      <p className="mt-2 text-xs">Types found: {props.analysis.widgetTypes.join(', ')}</p>
    </div>
  )
}

export function Summary(props: { analysis: AppAnalysis; focus: FocusRequest | null; onShowFinding: ShowFinding }) {
  const { analysis } = props
  const { widgets } = analysis
  /* One page configured in five places is one thing to review but five of the widgets counted in the total. */
  const placements = widgets.reduce((total, widget) => total + widget.paths.length, 0)

  /* No overflow-hidden on the card: it would silently disable every sticky element inside. */
  return (
    <header className="mb-4 rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="p-3 [@container(min-width:48rem)]:p-4">
        <p className="leading-relaxed [@container(min-width:48rem)]:text-base">
          Code Widgets are HTML pages configured into this app. Inspector Gadget reads their source and reports what
          they do — what they send out, what they take from the platform, and what they ask of the person using them.
          Every check it makes is listed in the catalogue below.
        </p>
        <p className="mb-0 mt-2 text-xs leading-relaxed text-muted-foreground">{analysis.caveat}</p>
      </div>

      {widgets.length > 0 && (
        <div className="border-t border-border">
          <HeaderSection title="Catalogue of checks" aside={`${FINDING_CATALOGUE.length} checks`}>
            <Catalogue />
          </HeaderSection>

          {/* Only the leading number is deduplicated by page, so it alone is labelled "unique". */}
          <HeaderSection
            title="Detected Code Widgets"
            aside={
              <>
                {widgets.length} unique, used in {placements} {placements === 1 ? 'place' : 'places'}, of{' '}
                {analysis.widgetsScanned} widgets
                {analysis.componentWidgets > 0 &&
                  `, where ${analysis.widgetsInComponents} are part of ${analysis.componentWidgets} Component widgets`}
              </>
            }
          >
            <AppTree widgets={widgets} onShowWidget={(index) => props.onShowFinding(index, null, null)} />
          </HeaderSection>

          <HeaderSection title="Findings" aside={<FindingCounts counts={analysis.findingCounts} />}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Summary for all Code Widgets
            </h3>
            <AppRiskAccordions
              risks={analysis.risks}
              reviewIds={analysis.reviewFindingIds}
              widgets={widgets}
              onShowFinding={props.onShowFinding}
            />
            <StatusLegend />

            <h3 className="mb-2 mt-8 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Per Code Widget
            </h3>
            {widgets.map((widget, index) => (
              <HeaderSection
                key={widget.paths[0]}
                nested
                className="mt-2"
                title={widgetLabel(widget, index)}
                aside={
                  <span className="flex items-center gap-2">
                    {widget.paths.length > 1 && <span>{widget.paths.length}&times; in this app</span>}
                    <span>{widget.htmlLines} lines</span>
                  </span>
                }
              >
                <WidgetSection
                  widget={widget}
                  index={index}
                  focus={props.focus?.index === index ? props.focus : null}
                />
              </HeaderSection>
            ))}
          </HeaderSection>
        </div>
      )}
    </header>
  )
}

/** A collapsible band of the summary card. Nested bands sit on the card surface, top-level ones on muted. */
function HeaderSection(props: {
  title: string
  aside?: React.ReactNode
  nested?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <details
      className={mergeClasses(
        'group/section border-border',
        { 'border-t': !props.nested, 'rounded-md border bg-card': props.nested },
        props.className
      )}
    >
      <summary className="flex cursor-pointer list-none items-start gap-2 px-3 py-2 hover:bg-muted/50">
        <SvgIcon
          icon={ChevronRight}
          size={14}
          className="mt-px shrink-0 text-muted-foreground transition-transform group-open/section:rotate-90"
        />
        {/* Wraps as a pair: in a narrow panel the aside drops below the title rather than squeezing it. */}
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{props.title}</span>
          {props.aside && <span className="ml-auto text-xs text-muted-foreground">{props.aside}</span>}
        </span>
      </summary>

      {/* A different surface and a left rail tie the body back to the row that opened it, three levels deep. */}
      <div
        className={mergeClasses('border-t border-border px-2 py-3 [@container(min-width:48rem)]:px-3', {
          'bg-card': props.nested,
          'bg-muted/50': !props.nested,
        })}
      >
        <div className={mergeClasses({ 'border-l-2 border-border pl-2': !props.nested })}>{props.children}</div>
      </div>
    </details>
  )
}

/** The verdict in three numbers, counted in checks: the catalogue above already introduced that total. */
function FindingCounts(props: { counts: AppAnalysis['findingCounts'] }) {
  const { checks, passed, toConfirm, violations } = props.counts

  return (
    <span>
      out of {checks} checks: <span className={STATUS_STYLE.counts.passed}>{passed} passed</span>,{' '}
      <span className={mergeClasses({ [STATUS_STYLE.counts.toConfirm]: toConfirm > 0 })}>
        {toConfirm} need to be checked
      </span>
      ,{' '}
      <span className={mergeClasses({ [STATUS_STYLE.counts.violations]: violations > 0 })}>
        {violations} in direct violation
      </span>
    </span>
  )
}

/**
 * The app verdict. Every category is listed every time, cleared or not: an absent line reads as unchecked,
 * and silence is the one thing a security report should never use to mean "fine".
 */
function AppRiskAccordions(props: {
  risks: RiskVerdict[]
  /** Decided by the analyser: confirm-only in every widget where they fired. */
  reviewIds: FindingId[]
  widgets: CodeWidgetAnalysis[]
  onShowFinding: ShowFinding
}) {
  return (
    <div className="space-y-2">
      {props.risks.map((risk) => {
        const rows = findingsWatchedBy(risk.id).map((id) => ({
          id,
          title: catalogueEntry(id)?.title ?? id,
          severity: (catalogueEntry(id)?.severity ?? 'info') as Severity,
          review: props.reviewIds.includes(id),
          widgets: props.widgets
            .map((widget, index) => ({ widget, index }))
            .filter(({ widget }) => widget.findings.some((finding) => finding.id === id)),
        }))
        const fired = rows.filter((row) => row.widgets.length > 0).length

        return (
          <details
            key={risk.id}
            className="group rounded-md border border-border bg-card"
            open={risk.status !== 'clear'}
          >
            <RiskSummaryRow risk={risk} fired={fired} watched={rows.length} />

            <ul className="divide-y divide-border border-t border-border">
              {rows.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5">
                  {/* Agrees with the pill beside it: a red cross next to CONFIRM would say two things about one row. */}
                  <RowStatusIcon fired={row.widgets.length > 0} review={row.review} />
                  {/* Only once fired: a severity on a cleared row grades something that did not happen. */}
                  {row.widgets.length > 0 && <SeverityPill tone={row.review ? 'review' : row.severity} />}
                  <code className="font-mono text-xs text-primary">{row.id}</code>
                  <span className={row.widgets.length ? 'font-medium' : 'text-muted-foreground'}>{row.title}</span>

                  {row.widgets.length ? (
                    row.widgets.map(({ widget, index }) => (
                      <button
                        key={index}
                        type="button"
                        title={`Show ${row.id} in this widget`}
                        onClick={() => props.onShowFinding(index, row.id, risk.id)}
                        className="rounded-sm border border-primary/30 bg-primary/10 px-1.5 text-xs text-primary transition-colors hover:border-primary/60 hover:bg-primary/20"
                      >
                        {widgetLabel(widget, index)}
                      </button>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">not detected</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )
      })}
    </div>
  )
}
